import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Sparkles } from "lucide-react";
import GuestLayout from "@/components/GuestLayout";
import { getExperiences } from "@/lib/api";
import { getPropertyId, getPropertySlug } from "@/lib/store";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Send, MapPin, Tag } from "lucide-react";
import { toast } from "sonner";
import { createBooking, getSettings } from "@/lib/api";
import api from "@/lib/api";

export default function ExperiencesList() {
  const navigate = useNavigate();
  const propertyId = getPropertyId();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  
  // Modal states
  const [selectedExp, setSelectedExp] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    guest_name: "",
    room_number: "",
    whatsapp: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState(null);
  
  // Coupon states
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    if (propertyId) {
      getSettings(propertyId).then(setSettings).catch(console.error);
    }
  }, [propertyId]);

  const handleOpenDetail = (exp) => {
    setSelectedExp(exp);
    // Remove query params update, just open modal
  };
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const res = await api.post("/coupons/validate", {
        code: couponCode,
        property_id: propertyId
      });
      setAppliedCoupon({
        code: couponCode.toUpperCase(),
        discount_amount: res.data.discount_amount,
        discount_type: res.data.discount_type,
        discount_value: res.data.discount_value
      });
      toast.success("Coupon applied!");
    } catch (error) {
      setCouponError(error.response?.data?.detail || "Invalid coupon code");
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createBooking({
        ...formData,
        experience_id: selectedExp.id,
        experience_title: selectedExp.title,
        property_id: propertyId,
        ...(appliedCoupon && {
          coupon_code: appliedCoupon.code,
          discount_amount: appliedCoupon.discount_amount || appliedCoupon.discount_value
        })
      });
      setShowBookingForm(false);
      setShowSuccessModal(true);
    } catch (error) {
      toast.error(error.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const fetchExperiences = async () => {
      try {
        const data = await getExperiences(propertyId);
        setExperiences(data);
      } catch (error) {
        console.error("Error fetching experiences:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchExperiences();
  }, [propertyId]);

  const categories = ["all", ...new Set(experiences.map((e) => e.category).filter(Boolean))];
  
  const filteredExperiences = activeCategory === "all" 
    ? experiences 
    : experiences.filter((e) => e.category === activeCategory);

  return (
    <GuestLayout>
      <div className="min-h-screen" data-testid="experiences-list">
        {/* Header */}
        <div className="page-header bg-white border-b border-[#E8E4D9]">
          <button
            onClick={() => navigate(`/${getPropertySlug()}`)}
            className="flex items-center gap-2 text-[#6B705C] mb-4 hover:text-[#2A9D8F] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 data-testid="page-title">Experiences</h1>
          <p>Discover curated adventures & activities</p>
        </div>

        {/* Feature Toggle Notice Banner */}
        {settings?.experiences_enabled === false && (
          <div className="mx-6 mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <p className="font-semibold flex items-center gap-1.5">
              <span>⚠️</span> Experience Bookings Currently Paused
            </p>
            <p className="text-xs text-amber-700 mt-1">
              Online booking is temporarily unavailable for this property. You may still browse itineraries or speak with the reception desk.
            </p>
          </div>
        )}

        {/* Categories */}
        <div className="px-6 py-4 overflow-x-auto">
          <div className="flex gap-3" data-testid="category-filters">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`category-pill whitespace-nowrap capitalize ${
                  activeCategory === category ? "active" : ""
                }`}
                data-testid={`category-${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Experiences Grid */}
        <div className="p-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="guest-card">
                  <Skeleton className="h-48 w-full" />
                  <div className="p-6">
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredExperiences.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-12 h-12 text-[#FFC107] mx-auto mb-4" />
              <p className="text-[#6B705C]">No experiences available at the moment</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
              {filteredExperiences.map((experience) => (
                <button
                  key={experience.id}
                  onClick={() => handleOpenDetail(experience)}
                  className="guest-card group text-left"
                  data-testid={`experience-card-${experience.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={experience.image_url || "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800"}
                      alt={experience.title}
                      className="w-full h-full object-cover img-zoom"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/60 to-transparent" />
                    
                    {/* Category Badge */}
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 text-[#264653] text-xs font-semibold uppercase tracking-wider">
                      {experience.category}
                    </span>

                    {/* Price */}
                    {experience.price && (
                      <span className="absolute bottom-4 right-4 px-3 py-1 rounded-full bg-[#FFC107] text-[#264653] font-semibold">
                        {experience.price}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-[#264653] mb-2 group-hover:text-[#2A9D8F] transition-colors">
                      {experience.title}
                    </h3>
                    <p className="text-[#6B705C] text-sm line-clamp-2 mb-3">
                      {experience.description}
                    </p>
                    
                    {experience.duration && (
                      <div className="flex items-center gap-2 text-[#6B705C] text-sm">
                        <Clock className="w-4 h-4" />
                        <span>{experience.duration}</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

        {/* Detail Modal */}
        <Dialog open={!!selectedExp && !showBookingForm && !showSuccessModal} onOpenChange={(open) => !open && setSelectedExp(null)}>
          <DialogContent className="sm:max-w-xl p-0 overflow-hidden bg-white rounded-3xl gap-0 border-0 shadow-2xl">
            {selectedExp && (
              <>
                <div className="relative h-64 sm:h-80 w-full">
                  <img
                    src={selectedExp.image_url || "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800"}
                    alt={selectedExp.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://images.unsplash.com/photo-1590091014590-70dfccb5295e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#264653]/90 via-[#264653]/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-semibold uppercase tracking-wider border border-white/30">
                        {selectedExp.category}
                      </span>
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl leading-tight mb-2">
                      {selectedExp.title}
                    </h1>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between gap-4 mb-6 pb-6 border-b border-[#E8E4D9]/50">
                    <div className="flex items-center gap-2 text-[#2A9D8F] font-medium">
                      <Clock className="w-5 h-5" />
                      <span>{selectedExp.duration || "Flexible"}</span>
                    </div>
                    {selectedExp.price && (
                      <div className="flex items-center gap-2 text-[#FFC107] font-semibold text-lg">
                        <Tag className="w-5 h-5" />
                        <span>{selectedExp.price}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="prose prose-sm sm:prose-base text-[#6B705C] mb-8 leading-relaxed max-h-[30vh] overflow-y-auto pr-2 custom-scrollbar">
                    {selectedExp.description}
                  </div>
                  
                  <Button
                    className={`w-full h-14 text-lg rounded-2xl shadow-lg ${
                      settings?.experiences_enabled === false
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                        : "btn-primary shadow-[#2A9D8F]/20"
                    }`}
                    disabled={settings?.experiences_enabled === false}
                    onClick={() => settings?.experiences_enabled !== false && setShowBookingForm(true)}
                  >
                    <Send className="w-5 h-5 mr-2" />
                    {settings?.experiences_enabled === false ? "Booking Paused" : "Request Booking"}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Booking Form Modal */}
        <Dialog open={showBookingForm} onOpenChange={setShowBookingForm}>
          <DialogContent className="sm:max-w-md bg-white rounded-3xl max-h-[90vh] overflow-y-auto custom-scrollbar">
            <DialogHeader className="pt-2">
              <DialogTitle className="font-serif text-2xl text-[#264653]">
                Request Booking
              </DialogTitle>
              <DialogDescription className="text-[#6B705C]">
                Fill in your details and we'll confirm your booking via WhatsApp.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 mt-2 pb-2">
              <div className="space-y-1.5">
                <Label htmlFor="guest_name" className="text-[#264653] font-medium">Your Name *</Label>
                <Input
                  id="guest_name"
                  value={formData.guest_name}
                  onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                  className="hm-input bg-gray-50/50"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="room_number" className="text-[#264653] font-medium">Room Number *</Label>
                <Input
                  id="room_number"
                  value={formData.room_number}
                  onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                  className="hm-input bg-gray-50/50"
                  placeholder="101"
                  required
                />
              </div>

              <div className="space-y-3 pt-3 border-t border-[#E8E4D9]/50">
                <Label className="text-[#264653] font-medium">Have a coupon?</Label>
                {!appliedCoupon ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Enter code" 
                        value={couponCode} 
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        className="uppercase font-mono bg-gray-50/50"
                      />
                      <Button 
                        type="button"
                        onClick={handleApplyCoupon} 
                        disabled={couponLoading || !couponCode.trim()}
                        className="bg-[#FFC107] hover:bg-[#F5B700] text-[#264653] font-medium"
                      >
                        {couponLoading ? "..." : "Apply"}
                      </Button>
                    </div>
                    {couponError && <span className="text-red-500 text-sm">{couponError}</span>}
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-50 text-green-700 p-3 rounded-xl border border-green-200">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{appliedCoupon.code}</span>
                      <span className="text-sm font-medium">
                        ({appliedCoupon.discount_type === 'percent' ? `${appliedCoupon.discount_value}% OFF` : `₹${appliedCoupon.discount_value} OFF`})
                      </span>
                    </div>
                    <button type="button" onClick={() => { setAppliedCoupon(null); setCouponCode(""); }} className="text-green-700 hover:text-red-500 font-bold px-2">
                      ✕
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-1.5 pt-3 border-t border-[#E8E4D9]/50">
                <Label htmlFor="whatsapp" className="text-[#264653] font-medium">WhatsApp Number *</Label>
                <Input
                  id="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  className="hm-input bg-gray-50/50"
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="notes" className="text-[#264653] font-medium">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="hm-input bg-gray-50/50 min-h-[80px]"
                  placeholder="Any special requests or preferences..."
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary h-12 text-lg rounded-xl shadow-md"
                disabled={submitting}
              >
                {submitting ? "Sending Request..." : "Confirm Booking"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Success Modal */}
        <Dialog open={showSuccessModal} onOpenChange={(open) => {
          setShowSuccessModal(open);
          if (!open) {
            setSelectedExp(null);
            setFormData({ guest_name: "", room_number: "", whatsapp: "", notes: "" });
            setCouponCode("");
            setAppliedCoupon(null);
          }
        }}>
          <DialogContent className="sm:max-w-md bg-white rounded-3xl text-center p-8 border-0 shadow-2xl">
            <div className="w-20 h-20 bg-green-50 text-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <Sparkles className="w-10 h-10" />
            </div>
            <DialogTitle className="font-serif text-3xl text-[#264653] mb-3">
              Booking Request Received!
            </DialogTitle>
            <DialogDescription className="text-[#6B705C] text-base space-y-4">
              <p>Hello <strong className="text-[#264653]">{formData.guest_name}</strong>, your request for <strong className="text-[#264653]">{selectedExp?.title}</strong> has been received.</p>
              <p className="text-[#264653] font-medium">Our team and local partners are working on it right now. We will get back to you with confirmation and your payment link on WhatsApp shortly!</p>
            </DialogDescription>
            <Button 
              className="w-full btn-primary mt-8 h-12 rounded-xl"
              onClick={() => {
                setShowSuccessModal(false);
                setSelectedExp(null);
                setFormData({ guest_name: "", room_number: "", whatsapp: "", notes: "" });
              }}
            >
              Back to Experiences
            </Button>
          </DialogContent>
        </Dialog>

    </GuestLayout>
  );
}
